import React, { useState } from 'react';

const Form: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState<'success' | 'error'>('success');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/xdklogqy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setPopupType('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setPopupType('error');
      }
    } catch (error) {
      setPopupType('error');
    } finally {
      setIsSubmitting(false);
      setShowPopup(true);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  return (
    <section className="mx-auto px-4 md:px-10 lg:px-32 w-full bg-[#F6F6F6] pt-20 pb-20">
      <div className="max-w-2xl">
        {/* Form Header */}
        <h2 className="text-[25px] md:text-[40px] lg:text-[40px] font-[700] leading-[40px] text-[#000] mb-7 lg:text-left">
          Contact Info
        </h2>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-[16px] md:text-[18px] font-[600] text-[#000] mb-2">
              Name*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="enter your name here"
              className="w-full px-0 py-1 border-b-2 border-[#1A2A44] bg-transparent text-[16px] text-[#000] placeholder-[#607D8B] focus:outline-none focus:border-[#0B6054] transition-colors duration-300"
              required
            />
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-[16px] md:text-[20px] lg:text-[20px] text-[#000] leading-[29px] font-[400] mb-4 last:mb-0">
              Email*
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="enter your email address here"
              className="w-full px-0 py-1 border-b-2 border-[#1A2A44] bg-transparent text-[16px] text-[#000] placeholder-[#607D8B] focus:outline-none focus:border-[#0B6054] transition-colors duration-300"
              required
            />
          </div>

          {/* Message Field */}
          <div>
            <label htmlFor="message" className="block text-[16px] md:text-[18px] font-[600] text-[#000] mb-2">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="e.g: I want to know the next best picks"
              rows={6}
              className="w-full px-4 py-1 border-2 border-[#1A2A44] bg-white text-[16px] text-[#000] placeholder-[#607D8B] rounded-lg focus:outline-none focus:border-[#0B6054] transition-colors duration-300 resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full md:w-auto px-24 py-3 rounded-lg text-[16px] font-[700] uppercase tracking-wide transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#0B6054] focus:ring-opacity-50 ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-[#0B6054] text-white hover:bg-[#094a41]'
              }`}
            >
              {isSubmitting ? 'SENDING...' : 'SEND'}
            </button>
          </div>
        </form>
      </div>
      <div className="mt-24 pt-8 border-t border-gray-300 -mb-14"></div>

      {/* Success/Error Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-out">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ease-out scale-100">
            {/* Header */}
            <div className="relative p-6 border-b border-gray-100">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  popupType === 'success' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {popupType === 'success' ? (
                    <svg className="w-6 h-6 text-[#0B6054]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold ${
                    popupType === 'success' ? 'text-[#0B6054]' : 'text-red-800'
                  }`}>
                    {popupType === 'success' ? 'Message Sent!' : 'Error Sending Message'}
                  </h3>
                </div>
              </div>
              <button
                onClick={closePopup}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className={`text-sm ${
                popupType === 'success' ? 'text-[#0B6054]' : 'text-red-700'
              }`}>
                {popupType === 'success' 
                  ? 'Thank you for your message! We\'ll get back to you as soon as possible.'
                  : 'Sorry, there was an error sending your message. Please try again or contact us directly.'
                }
              </p>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={closePopup}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    popupType === 'success'
                      ? 'bg-[#0B6054] text-white'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {popupType === 'success' ? 'Great!' : 'Try Again'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Form;
